import React, { useEffect, useMemo } from 'react';

import { NavLink, useLocation } from 'react-router-dom';

import logOutLogo from 'assets/images/log-out.svg';
import editIcon from 'assets/images/properties/edit.svg';
import logo from 'assets/images/simple_logo.png';
import { ReturnComponentType } from 'common';
import { Paths, Roles } from 'enums';
import { useAppSelector, useTypedDispatch } from 'hooks';
import {
  fetchCertainCompany,
  logOut,
  selectCertainCompany,
  selectUser,
  selectUserRole,
} from 'state';
import { checkIsAdmin, checkIsSuperAdmin, getMenuRoutes } from 'utils';

import './SideBar.scss';

interface ISidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollased: (value: boolean) => void;
  setShowCompanyInfoModal: (value: boolean) => void;
  setShowEditUserModal: (value: boolean) => void;
}

export const SideBar = ({
  sidebarCollapsed,
  setSidebarCollased,
  setShowCompanyInfoModal,
  setShowEditUserModal,
}: ISidebarProps): ReturnComponentType => {
  const { pathname } = useLocation();
  const dispatch = useTypedDispatch();
  const company = useAppSelector(selectCertainCompany);
  const role = useAppSelector(selectUserRole);
  const user = useAppSelector(selectUser);

  const clickSideMenuCondition = (paths: Paths) => [Paths.MANAGEMENT].includes(paths);

  useEffect(() => {
    // fetch the company info to allow the user to edit it, if they have permission
    if (user.company?.id && (checkIsAdmin(role) || checkIsSuperAdmin(role))) {
      dispatch(fetchCertainCompany(user.company.id));
    }
  }, [user.company?.id]);

  const sideMenu = useMemo(() => {
    return getMenuRoutes([role]).map(menuRoute => {
      const menuRouteName =
        menuRoute.name === 'Users Management' && role === Roles.SUPER_ADMIN
          ? 'Manage Admins'
          : menuRoute.name;

      return (
        <div key={menuRoute.path} className="SideBar_Item">
          {clickSideMenuCondition(menuRoute.path) ? (
            <div
              className={
                pathname.includes(menuRoute.path)
                  ? 'SideBar_Item_Link SideBar_Item_Active'
                  : 'SideBar_Item_Link SideBar_Item_Inactive'
              }
            >
              <img src={menuRoute.icon} alt={menuRoute.name} />
              <p>{menuRouteName}</p>
              <div className="SideBar_Item_Empty" />
            </div>
          ) : (
            <NavLink
              to={menuRoute.path}
              className={navData =>
                navData.isActive
                  ? 'SideBar_Item_Link SideBar_Item_Active'
                  : 'SideBar_Item_Link SideBar_Item_Inactive'
              }
            >
              <img src={menuRoute.icon} alt={menuRoute.name} />
              <p>{menuRouteName}</p>
              <div className="SideBar_Item_Empty" />
            </NavLink>
          )}
        </div>
      );
    });
  }, [pathname, role]);

  const onLogoutClick = () => {
    dispatch(logOut());
  };

  const onToggleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // when on the inspection page, blur the toggle after clicking it so the video will be ready to play with the space button
    if (pathname.startsWith('/inspection/')) {
      (event.target as HTMLButtonElement).blur();
    }
    setSidebarCollased(!sidebarCollapsed);
  };

  const canEditCompanyInfo = useMemo(() => {
    return role === Roles.COMPANY_ADMIN;
  }, [role]);

  return (
    <div className={`SideBar ${sidebarCollapsed ? 'Collapsed' : ''}`}>
      <button className="SideBar_Toggle" type="button" onClick={onToggleClick}>
        {sidebarCollapsed ? '❯' : '❮'}
      </button>
      <img src={logo} alt="logo" className="SideBar_Logo" />
      {!sidebarCollapsed && !canEditCompanyInfo && (
        <div className="SideBar_Company">
          {checkIsSuperAdmin(user.role) ? 'Admin Console' : user.company?.name || ''}
        </div>
      )}
      {!sidebarCollapsed && canEditCompanyInfo && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className="SideBar_Company SideBar_Company_Link"
          onClick={() => setShowCompanyInfoModal(true)}
        >
          {checkIsSuperAdmin(user.role) ? 'Admin Console' : company.name || ''}
          <img src={editIcon} alt="edit-icon" className="SideBar_Company_EditIcon" />
        </div>
      )}
      {sideMenu}
      <div className="SideBar_LogOut">
        <div>
          {user?.firstName && ( // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className="SideBar_LogOut_Name"
              onClick={() => setShowEditUserModal(true)}
            >
              {sidebarCollapsed ? (
                <div className="SideBar_LogOut_Name_Initials">
                  {user.firstName[0]}
                  {user.lastName[0]}
                </div>
              ) : (
                <>
                  <span>
                    {user.firstName} {user.lastName}
                  </span>
                  <img
                    src={editIcon}
                    alt="edit-icon"
                    className="SideBar_LogOut_EditIcon"
                  />
                </>
              )}
            </div>
          )}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div className="SideBar_LogOut_Link" onClick={onLogoutClick}>
            <img src={logOutLogo} alt="log-out" className="SideBar_LogOut_Icon" />
            <p>Log Out</p>
          </div>
        </div>
      </div>
    </div>
  );
};
